import { getAddress } from '@ethersproject/address'
import { BigNumber } from '@ethersproject/bignumber'
import { ChainId, Currency, CurrencyAmount, JSBI, Percent } from '@nominex/nomiswap-sdk'
import { BIPS_BASE } from 'config/constants'
import { TokenAddressMap } from 'state/lists/hooks'
import { CHAINS } from 'libs/wagmi'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getBlockExplorerLink(
  data: string | number,
  type: 'transaction' | 'token' | 'address' | 'block' | 'countdown',
  chainId: ChainId = ChainId.BSC,
): string {
  const chain = CHAINS.find(({ id }) => id === chainId)

  switch (type) {
    case 'transaction': {
      return `${chain.blockExplorers.default.url}/tx/${data}`
    }
    case 'token': {
      return `${chain.blockExplorers.default.url}/token/${data}`
    }
    case 'block': {
      return `${chain.blockExplorers.default.url}/block/${data}`
    }
    case 'countdown': {
      return `${chain.blockExplorers.default.url}/block/countdown/${data}`
    }
    default: {
      return `${chain.blockExplorers.default.url}/address/${data}`
    }
  }
}

export function getBlockExplorerName(chainId: ChainId = ChainId.BSC) {
  const chain = CHAINS.find(({ id }) => id === chainId)

  return chain.blockExplorers.default.name
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(JSBI.BigInt(num), BIPS_BASE)
}

export function calculateSlippageAmount(value: CurrencyAmount<Currency>, slippage: number): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }

  return [
    JSBI.divide(JSBI.multiply(value.quotient, JSBI.BigInt(10000 - slippage)), BIPS_BASE),
    JSBI.divide(JSBI.multiply(value.quotient, JSBI.BigInt(10000 + slippage)), BIPS_BASE),
  ]
}

export function isPairWithCurrenciesExist(pairs: any, currencyA: Currency, currencyB: Currency): boolean {
  return (
    currencyA &&
    currencyB &&
    pairs.some(
      ([token0, token1]) =>
        (token0.equals(currencyA) || token0.equals(currencyB)) &&
        (token1.equals(currencyA) || token1.equals(currencyB)),
    )
  )
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  if (currency?.isNative) return true
  return Boolean(currency?.isToken && defaultTokens[currency.chainId]?.[currency.address])
}

// Re-export viem utilities for new code (backwards compatible with ethers)
export {
  formatUnits as formatUnitsViem,
  parseUnits as parseUnitsViem,
  formatEther as formatEtherViem,
  parseEther as parseEtherViem,
  isAddress as isAddressViem,
  getAddress as getAddressViem,
  toBigInt,
  bigIntToNumber,
} from './viem'
